/**
 * ProblemBodyPreview — 문제 본문 미리보기 **한 벌**.
 *
 * 좌측 검색 카드와 가운데 담은 문항이 **같은 얼굴**이어야 한다(오너 지시 2026-08-28
 * 「이 미리보기가 저 요소 안에도 들어가게」). 그래서 그리는 마크업을 여기 한 곳에 두고 둘이
 * 함께 쓴다 — 클래스를 두 벌로 두면 한쪽만 고쳐도 빌드는 통과하고 화면도 떠서 조용히 갈린다.
 *
 * 받는 것
 *   html      조판된 본문 HTML(Lexical→HTML). 없으면 null.
 *   fallback  평문 요약. 문자열도 노드도 받는다 — 검색 카드가 「본문 미리보기 없음」 회색
 *             자리표시를 노드로 넘긴다. html 이 없을 때만 그린다.
 *   compact   좁고 촘촘한 목록(가운데 열)용. 아래 참고.
 *
 * 둘 다 없으면 **아무것도 그리지 않는다** — 빈 상자가 행 높이만 먹는다.
 * 다만 compact 은 반대다: 아무것도 없어도 칸을 세운다(아래 ① — 높이가 하한이기도 하다).
 * html 이 빈 문자열이어도 없는 것으로 본다(변환 결과가 빈 본문이면 평문 폴백이 살아야 한다).
 *
 * ══ 폭 — **지면의 한 단에 맞춘다**(오너 지시 2026-08-28) ═══════════════════
 * 전에는 폭을 카드에 맡겼다. 그러면 줄바꿈이 종이와 달라져, 화면에서 두 줄인 발문이 지면에서는
 * 세 줄이 된다 — 「보이는 것 = 나오는 것」이 깨진다. 조립 창은 **무엇이 어떻게 앉을지**를 보는
 * 자리라 그 어긋남이 곧 오판이 된다.
 *
 * 그래서 본문을 **지면 한 단 폭 그대로 그리고**, 그것을 칸에 맞게 **통째로 축소**한다.
 * 줄바꿈·수식 줄넘김·그림 크기가 종이와 같은 자리에서 일어나고, 눈에 닿는 크기만 작아진다.
 *
 * 축소는 **transform** 이다. zoom 은 mm 를 px 로 바꾼 뒤 배율을 먹여 1px 안팎의 반올림이
 * 남고(같은 이유를 PaperComposeModal 의 우측 지면 주석이 적어 두었다), transform 은 레이아웃을
 * 건드리지 않아 줄바꿈이 배율에 따라 흔들리지 않는다. 대신 **차지하는 높이가 줄지 않으므로**
 * 감싸는 칸의 높이를 축소된 높이로 직접 지정해야 한다(안 하면 아래가 텅 빈다).
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ── compact 가 하는 일 ────────────────────────────────────────────────────
 * 가운데 열은 한 화면에 열 줄 넘게 서는 목록이라 본문을 통째로 펴면 목록이 무너진다.
 *  ① 높이를 **h-16(64px)로 못 박는다.** max-h 만 걸면 본문 길이에 따라 행이 제각각이 되어
 *     목록이 계단처럼 된다 — 본문이 없는 행 · 아직 안 온 행 · 긴 행이 나란히 서기 때문이다.
 *     상한이자 하한이라 본문이 뒤늦게 도착해도 행 높이가 들썩이지 않는다.
 *     **표에서는 `fill` 로 이 못을 뺀다** — 거기서는 행 높이를 개념 배지 같은 옆 칸이 정하므로,
 *     본문은 그 높이를 받아 채우기만 하면 된다(COMPACT_BOX_FILL 주석).
 *     **`clamp={false}` 는 못을 아예 뽑는다** — 잘리지 않고 다 보이는 대신 행 높이가 제각각이
 *     된다. 그 값을 치를 만한 자리에서만 쓴다(CompactBody 의 shown 주석).
 *  ② 잘리는 자리를 **아래로 흐리게** 해 「더 있다」가 보이게 한다. 행마다 스크롤바를
 *     띄우지 않는 이유는 열 줄이면 스크롤바가 열 개이기 때문이다.
 *     덮개가 아니라 **마스크**라 행 바탕이 무엇이든(흰색·amber-50·primary/5) 그 색 그대로 흐려진다.
 *  ③ 그림은 지면과 같은 규칙으로 단 폭에 맞춘다.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
// 본문 조판 규칙 — 폭·글자만 지면에 맞추고 조판을 안 대면 **선지가 세로로 선다.**
// 무엇의 사본이고 왜 이 값인지는 그 파일 머리말에 있다.
import './problemBodyPaper.css';

/**
 * 지면에서 **본문이 실제로 차지하는 폭**(px, 배율 1).
 *
 * 한 단(`.problems-column`)의 폭이 아니다. 지면의 문항은 가로로 눕는 flex 라 그 안이 갈린다 —
 * `.problem-item { max-width: 488px; display:flex; gap:28px }` 에 `.problem-number { min-width:36px }`
 * (printPreview.js:1735·1800). 남는 것이 본문 칸이다.
 *
 *   488 − 37(번호 실측) − 28(gap) = **423**
 *
 * 실측(2026-08-28, 시험지 #23 · dev): `.problem-content` 의 offsetWidth 가 **423**,
 * 그 안 문단의 계산된 글자 크기가 **18px** · 줄높이 **27px** 였다.
 * 종전에는 단 폭 533 에 11~12px 로 그려 **개행이 지면과 애초에 갈려 있었다** — 이 부품이
 * 없애려던 바로 그 어긋남이다. 폭과 글자를 함께 지면 값으로 맞춰야 줄이 같은 자리에서 꺾인다.
 *
 * 덤으로 글자가 커진다: 배율은 `칸 너비 / 이 값`이고 화면 글자는 `18 × 배율`이라,
 * 기준이 533→423 으로 좁아지면 같은 칸에서 배율이 1.26배 오른다.
 */
export const PAPER_BODY_PX = 423;

/**
 * **현행 인쇄 v4(A4)가 실제로 본문에 주는 폭**(px, 배율 1). 위 423 과는 다른 값이다.
 *
 *   printSpec `getSheetGeometry('A4')`  콘텐츠폭 196mm · k=196/296=0.662162
 *                                       프레임안쪽 7mm · 단간격 16k=10.595mm
 *                                       한 단 colW = (196 − 14 − 10.595)/2 = 85.703mm = 323.91px
 *   PrintSheetV4 `numberSpecV4`         번호 칸 minWidth = 27k pt = 17.878pt = 23.84px
 *                                       번호–본문 간격 = 7.4k mm = 4.900mm = 18.52px
 *   → 본문 = 323.91 − 23.84 − 18.52 = **281.55px**
 *
 * 숫자를 여기 적어 두는 이유는 PAPER_BODY_PX 와 같다 — 지면 모듈(PrintSheetV4)을 끌어오면
 * 목록 화면 번들에 인쇄 엔진이 통째로 실린다. 지면 규격이 바뀌면 이 값도 다시 재야 한다.
 */
const PAPER_V4_BODY_PX = 281.55;

/**
 * 이 부품이 **지면을 몇 배로 확대해 그리는가**. 손으로 적지 않고 위 두 값에서 낸다.
 *
 *   423 / 281.55 = **1.5024**
 *
 * 글자·수식·여백은 전부 em·mm 라 이 배율을 알아서 따라온다 — 그래서 지금까지 필요가 없었다.
 * **그림만 절대 픽셀이라 안 따라온다.** 310px 그림이 지면에서는 단(281.55)에 걸려 폭을 꽉
 * 채우는데(100%), 화면에서는 423 안의 310px 이라 73.3% 만 차지한다. 그러면 그림이 든 문제가
 * 종이보다 **짧아 보이고**, 조립 창에서 「이만큼 들어간다」를 낙관하게 된다 —
 * 이 부품이 막으려던 바로 그 오판이다. 그래서 그림에만 이 배율을 먹인다(problemBodyPaper.css).
 */
export const PAPER_ZOOM = PAPER_BODY_PX / PAPER_V4_BODY_PX;

/**
 * 지면 본문의 글자 — 위와 같은 실측이다. 이 값이 어긋나면 폭이 맞아도 줄이 다른 데서 꺾인다.
 *
 * 여기가 이 숫자의 **정본**이다 — problemBodyPaper.css 는 자손 글자를 `inherit` 으로 두어
 * 18 을 한 벌 더 적지 않는다.
 *
 * (종전 주석은 「KaTeX 는 `font-size: 1em` 이라 이 값을 따라온다 — printPreview.js:1460」
 *  이었다. **그 전제는 이 부품에서 참이 아니었다** — 그 규칙은 인쇄 미리보기가 제 창에 심는
 *  스타일 안에 있어 이 카드에 닿지 않았고, KaTeX 기본값 1.21em 이 그대로 살아 수식만 21.78px
 *  로 섰다. 지금은 problemBodyPaper.css 가 같은 규칙을 대므로 참이다.)
 */
const PAPER_TEXT = 'text-[18px] leading-[27px]';

/**
 * 목록의 배율 상한 — **넓은 표와 좁은 카드가 같은 크기로 서게 하는 값**
 * (오너 지시 2026-08-30 「확장됐을때의 크기와 동일하게」).
 *
 * 문제 목록은 폭에 따라 얼굴이 둘이다. 문제를 닫아 목록이 화면을 다 쓰면 표(ProblemTable),
 * 문제를 열어 목록이 좁아지면 카드(ProblemSearchPanel)다. 배율은 `칸 너비 / 423` 이라
 * 상한을 한쪽에만 두면 **같은 목록인데 본문 글자가 뛴다** — 실측 2026-08-30:
 * 카드 칸 382px → 배율 0.903 → 글자 16.3px / 표 칸은 423 보다 넓어 배율 1 → 18px.
 * 표에만 0.67 이 걸려 있어 12px 였고, 문제를 열고 닫을 때마다 본문 크기가 갈렸다.
 *
 * 값의 근거는 표에서 정한 그대로다: 18 × 0.67 ≈ 12px 라 **옆 칸 글자와 같은 크기**가 되고,
 * 덤으로 h-16 안에 보이는 줄이 2.4 → 3.6 줄로 늘어난다.
 * 두 곳이 이 상수 하나를 함께 쓴다 — 숫자를 두 벌로 두면 한쪽만 고쳐도 조용히 갈린다.
 */
export const LIST_BODY_MAX_SCALE = 0.67;

/** 본문 칸 — 상한이자 하한인 고정 높이(위 ①). 상자(테두리)는 바깥 래퍼가 갖는다 */
const COMPACT_BOX = 'relative h-16 overflow-hidden';
/** 다 펴 본 칸 — 높이 제한을 걷는다(마스크도 함께 걷는다. 가릴 것이 없다) */
const COMPACT_BOX_OPEN = 'relative overflow-hidden';
/**
 * 좁은 목록의 본문을 감싸는 **상자** — 오너 지시 2026-08-31 「바탕이나 외곽선을 추가해서
 * 강조」. 검색 카드(SEARCH_CARD)와 같은 옷이고 모서리만 한 단 작다(12px) — 이 칸이 서는 곳은
 * 행 안이라 카드 안의 카드가 되기 때문이다.
 *
 * **테두리를 host 가 아니라 이 래퍼가 갖는 이유**: host 에는 흐림막(마스크)이 걸려 있고
 * 마스크는 요소 전체를 먹으므로, 거기에 테두리를 주면 아래쪽 선이 함께 흐려져 상자가 터진다.
 * 여백은 **가로만** 준다. 세로 여백은 이 칸의 높이가 상한이자 하한(h-16)이라 그만큼 본문이
 * 잘리는데, 가로는 폭을 6px 씩 줄일 뿐이고(배율이 3~4% 내려간다) 글자가 선에 붙지 않는다.
 */
const COMPACT_CARD = 'mt-1 overflow-hidden rounded-xl border border-gray-300 bg-white px-1.5';
/**
 * **행 높이를 받아 채우는 칸**(fill) — 표에서 쓴다(오너 지시 2026-08-29).
 *
 * 표의 행 높이는 본문이 정하지 않는다. 개념 배지가 세 줄이면 그 칸이 행을 늘리고, 그때
 * h-16 으로 못박힌 본문 아래에는 **쓰지 않는 빈 자리**가 남는다. 어차피 생긴 높이라면
 * 본문이 쓰는 편이 낫다 — 같은 행에서 본문이 더 보이고, 「본문 모두 보기」를 누를 일이 준다.
 * 그래서 이 모드는 높이를 **위에서 받고**(h-full) 바닥만 h-16 으로 지킨다.
 * 위쪽 여백(mt-1)이 없는 것은 이 칸이 셀의 높이를 정확히 채워야 하기 때문이다.
 * ⚠ h-full 이 서려면 감싸는 `<td>` 가 명시적 높이를 가져야 한다 — 표 쪽에서 `h-px` 로 준다
 *   (테이블 셀의 height 는 최소값으로 읽혀, 실제로는 행 높이가 내려온다).
 */
const COMPACT_BOX_FILL = 'relative h-full min-h-16 overflow-hidden';
/**
 * 잘림 판정의 하한 — **칸을 직접 재기 전까지만** 쓰는 임시값이다.
 *
 * 전에는 여기에 64 를 박아 두고 「h-16 = 4rem = 64px」로 셈했다. 데스크톱에서는 맞다.
 * 그런데 이 저장소의 CSS 는 **`@media (max-width:767px)` 안에서 `html{font-size:90%}`** 를 건다
 * (index.css). 그 폭 아래에서 1rem 은 14.4px 이고 `h-16` 은 **57.6px** 가 된다.
 * 그러면 축소 높이가 58~66px 인 본문은 **칸에서는 잘리는데 [본문 모두 보기]가 안 뜬다** —
 * 마지막 줄이 흐림막 뒤로 조용히 사라지고 펼 길이 없다.
 *
 * 상수를 하나 더 적어 두 폭을 갈라 셈하는 대신 **칸의 실제 높이를 잰다**(CompactBody 의 boxH).
 * rem 이 걸린 값을 px 로 되받아 적는 순간 그 가정이 언제 깨지는지 아무도 모르게 된다.
 * 이 값은 아직 붙지 않아 잴 수 없는 첫 렌더에만 쓰인다.
 */
const COMPACT_FALLBACK_H = 64;

/**
 * 검색 카드의 본문을 감싸는 상자 — 오너 지시 2026-08-28 「이 요소내의 **문제만** 카드
 * 디자인으로 보이게 묶어줘」. 모서리는 처음 24(3xl)로 넣었다가 **오너가 직접 16(2xl)으로
 * 내렸다**(같은 날). 16 은 2026-08-24 결정으로 이 저장소 카드 단의 값이라 예외가 아니다.
 *
 * 감싸는 것은 본문 하나다. 문제 번호·배지·라벨은 상자 밖에 남는다 — 지시가 「문제만」이었고,
 * 배지까지 넣으면 카드가 행 전체가 되어 목록의 행 구분(li 테두리)과 두 겹이 된다.
 *
 * 모서리는 16px(rounded-2xl)다 — 오너 지시 2026-08-28 로 24px(3xl)에서 내렸다.
 * 이 저장소가 카드 단의 실효값으로 이미 쓰는 값이라(CLAUDE.md 2026-08-24 행) 예외가 필요 없어졌다.
 *
 * 테두리는 **gray-300**(오너 지시 2026-08-31 「문제 본문 영역만 좀 더 강조」). 종전 gray-200 은
 * 이 상자를 담은 행의 테두리와 **같은 값**이라 두 겹이 한 층으로 읽혔다 — 어디까지가 행이고
 * 어디부터가 문제인지 선만 보고는 갈리지 않았다. 그림자를 얹지 않은 것은 이 저장소가 쓰는
 * 그림자가 lg·md 두 단뿐이고(둘 다 창·띄운 것의 값이다) 목록 안 카드에 맞는 단이 없어서다.
 */
const SEARCH_CARD = 'mt-1 overflow-hidden rounded-2xl border border-gray-300 bg-white p-2.5';

/**
 * 잘린 자리를 흐리는 법(위 ②) — **덮개가 아니라 마스크**다.
 * 흰색으로 끝나는 그라데이션을 위에 얹으면 행 바탕이 흰색일 때만 맞는다. 이 목록의 행은
 * 상황에 따라 amber-50(삭제된 문항)·primary/5(바꿀 자리)·primary/5(끌어 놓는 중)로 물드는데,
 * 그때 흰 띠가 그 위에 떠서 **아래 16px 만 색이 빠진 자국**으로 남는다(끌고 있을 때 특히 눈에 든다).
 * 마스크는 글자 자체를 투명하게 만들어 **바탕이 무엇이든 그 색 그대로** 사라진다.
 */
const COMPACT_FADE_MASK = '[-webkit-mask-image:linear-gradient(to_bottom,black_calc(100%-16px),transparent)] [mask-image:linear-gradient(to_bottom,black_calc(100%-16px),transparent)]';

/**
 * 본문을 **지면 한 단 폭으로 그리고 칸에 맞게 축소**한다.
 *
 * 돌려주는 것
 *   hostRef  폭을 재는 자리(감싸는 칸). 이 폭이 곧 배율의 분자다.
 *   innerRef 지면 폭으로 그려진 알맹이. 높이를 재서 축소된 높이를 낸다.
 *   scale    0<k≤1. 칸이 지면보다 넓어도 1 을 넘기지 않는다 — 확대하면 종이보다 크게 보인다.
 *   scaledH  축소된 뒤의 높이(px). 감싸는 칸의 높이로 그대로 쓴다.
 *
 * transform 은 레이아웃 크기를 바꾸지 않으므로 innerRef 의 높이는 배율과 무관하다 —
 * 그래서 host 와 inner 를 동시에 지켜봐도 되먹임(무한 루프)이 생기지 않는다.
 * 첫 페인트 전에 재려고 useLayoutEffect 를 쓴다(useEffect 면 한 프레임 동안 배율 1 로 커 보인다).
 */
function usePaperFit(maxScale = 1) {
  const hostRef = useRef(null);
  const innerRef = useRef(null);
  const [fit, setFit] = useState({ scale: 1, scaledH: 0 });

  useLayoutEffect(() => {
    const host = hostRef.current;
    const inner = innerRef.current;
    if (host == null || inner == null) return undefined;
    const measure = () => {
      // ⚠ clientWidth 는 **안쪽 여백을 포함한다.** 카드가 p-2.5(좌우 10px)를 두르고 있어
      // 그 값으로 배율을 내면 축소된 본문이 내용 상자보다 20px 넓어져 **오른쪽이 잘린다.**
      // 그래서 여백이 없는 자리를 따로 두고 그것을 잰다(아래 measureRef) — 여기 오는 host 가 그것이다.
      const w = host.clientWidth;
      if (!w) return; // 아직 안 붙었다 — 다음 관찰에서 잰다
      // 상한은 보통 1 이다 — 확대하면 종이보다 크게 보인다. **표처럼 칸이 지면보다 넓은 자리**
      // 에서는 그 1 이 그대로 걸려 본문이 18px 로 서고, 옆 칸 글자(12px)보다 커진다.
      // 그런 자리는 maxScale 로 상한을 내려 받는다(오너 지시 2026-08-29 「배율을 작게」).
      const k = Math.min(maxScale, w / PAPER_BODY_PX);
      // 높이는 **넉넉히** 잡는다 — 「하단이 잘리지 않게」가 요구다(오너 2026-08-28).
      //  · scrollHeight 를 함께 보는 것은 넘쳐 나간 자식(넓은 표·수식)이 offsetHeight 에 안 잡히기 때문이다.
      //  · +1 은 소수점 반올림 몫이다. 실측(320px 칸·KaTeX 디스플레이 포함): offsetHeight 80 인데
      //    자식이 실제로 차지한 바닥은 81 이었다. 그 1px 이 마지막 줄을 칸 밖으로 민다.
      //    한 픽셀 남는 것은 눈에 닿지 않지만, 한 픽셀 모자라면 글자가 잘린다.
      const natural = Math.max(inner.offsetHeight, inner.scrollHeight);
      const h = Math.ceil(natural * k) + 1;
      // 같은 값이면 상태를 갈지 않는다 — ResizeObserver 가 매 프레임 도는 것을 막는다
      setFit((prev) => (Math.abs(prev.scale - k) < 0.0005 && prev.scaledH === h ? prev : { scale: k, scaledH: h }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // host = 칸이 넓어지거나 좁아질 때 / inner = 그림·수식이 늦게 떠 본문이 자랄 때
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [maxScale]);

  return { hostRef, innerRef, ...fit };
}

/**
 * 지면 폭 그대로 그리는 알맹이 — 축소는 감싸는 쪽이 style 로 건다.
 * 그릴 것이 없어도 **이 div 는 늘 붙어 있다.** 조건부로 빼면 본문이 늦게 도착했을 때
 * innerRef 가 비어 있던 탓에 ResizeObserver 가 영영 안 붙는다(그 행만 크기를 다시 안 잰다).
 */
function PaperWidthBody({ innerRef, scale, html, fallback, hasHtml, hasFallback }) {
  // 글자는 좁은 칸이든 넓은 칸이든 **지면 값 하나**다. 화면에서 작아 보이는 것은 칸이 좁아
  // 배율이 낮기 때문이고, 여기서 글자를 따로 줄이면 그만큼 줄이 다른 데서 꺾인다.
  const size = PAPER_TEXT;
  return (
    <div
      ref={innerRef}
      style={{
        // **흐름에서 뺀다.** transform 은 레이아웃 크기를 줄이지 않으므로, 이 div 가 흐름 안에
        // 있으면 감싸는 칸의 최소 내용 폭이 423px 로 굳는다. 표에서 그 값이 그대로 드러났다
        // (실측 2026-08-29: 표 폭을 900·1200·1400 으로 바꿔도 본문 칸이 **447px 고정** —
        //  423 + 좌우 여백 24. w-[26%]·min-w-72 를 줘도 내용이 이겨 아무 효과가 없었다).
        // absolute 로 띄우면 칸이 제 폭을 갖고, 배율은 그 폭을 따라 내려간다.
        // 감싸는 칸은 높이를 직접 받으므로(scaledH·h-16) 이 div 가 높이를 안 내도 무너지지 않는다.
        position: 'absolute',
        left: 0,
        top: 0,
        width: PAPER_BODY_PX,
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
        // 자식 여백이 이 상자 밖으로 새지 않게 서식 문맥을 만든다.
        // ⚠ 지금 이 저장소에서는 **차이가 나지 않는다** — Tailwind preflight 가 p 의 여백을 0 으로
        // 두고, KaTeX 디스플레이의 12px 여백은 본문 가운데라 새지 않는다(실측: flow-root 유무로
        // offsetHeight 80 동일). 그래도 두는 것은 본문이 서버에서 오는 남의 마크업이라,
        // 첫·마지막 자식에 여백이 붙는 순간 높이가 조용히 모자라기 때문이다.
        display: 'flow-root',
      }}
    >
      {hasHtml ? (
        // problem-body-paper — **지면의 조판 규칙 한 벌**(problemBodyPaper.css).
        // 종전에는 여기에 `[&_img]:h-auto [&_img]:max-w-full` 두 개만 있었다. 그림 두 줄만
        // 있고 선지·수식·상자 규칙이 없어, 폭과 글자는 지면인데 **조판은 지면이 아니었다.**
        // 그 두 줄도 이제 저 파일의 img 규칙이 지면 값 그대로 갖는다 — 두 벌로 두지 않는다.
        <div
          /* 글자는 **gray-900** 이다(오너 지시 2026-08-31 「문제 본문 영역만 좀 더 강조」).
             종전 gray-700 은 같은 카드의 개념 배지(gray-700·gray-800)·라벨(gray-600)과 거의
             같은 먹기라, 카드에서 가장 중요한 것과 곁다리가 한 층에 서 있었다.
             종이 쪽 값에 가까워지는 것이기도 하다 — 이 칸은 지면을 축소해 그리는 자리다. */
          className={`problem-body-paper ${size} text-gray-900`}
          // 그림에만 먹이는 확대 배율(위 PAPER_ZOOM). CSS 에 숫자를 심지 않고 여기서 내려보내
          // **배율의 정본을 한 곳에 둔다** — 값이 없으면 CSS 가 1 로 떨어져 종전 그대로 그린다.
          style={{ '--paper-zoom': String(PAPER_ZOOM) }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : hasFallback ? (
        <p className={`${size} text-gray-900`}>{fallback}</p>
      ) : null}
    </div>
  );
}

function ProblemBodyPreview({ html = null, fallback = null, compact = false, showToggle = true, maxScale = 1, fill = false, clamp = true, expanded, onExpandedChange }) {
  const hasHtml = typeof html === 'string' && html !== '';
  const hasFallback = fallback != null && fallback !== '';
  // 둘 다 없으면 그리지 않는다 — **단 compact 은 예외다.** compact 의 h-16 은 상한이자
  // 하한이라(위 ①) 여기서 null 을 돌려주면 그 행만 64px 짧게 섰다가 본문이 도착하는 순간
  // 뛴다. 서버가 준 시험지를 그대로 연 경우가 그 자리다 — toRows 가 preview·labels 를 둘 다
  // 빈 문자열로 채우는 행이 흔해, 목록 전체가 본문 도착과 함께 한 번 들썩인다.
  if (!hasHtml && !hasFallback && !compact) return null;
  if (compact) {
    return <CompactBody html={html} fallback={fallback} hasHtml={hasHtml} hasFallback={hasFallback} showToggle={showToggle} maxScale={maxScale} fill={fill} clamp={clamp} expanded={expanded} onExpandedChange={onExpandedChange} />;
  }
  return <SearchCardBody html={html} fallback={fallback} hasHtml={hasHtml} maxScale={maxScale} />;
}

/**
 * 카드 모드 — 높이 제한이 없다(한 건씩 읽는 자리라 다 편다). 폭만 지면에 맞춘다.
 *
 * 쓰는 곳이 둘이다: 조립 창의 좌측 **검색 카드**, 그리고 **문항 편집 목록의 행**
 * (오너 지시 2026-08-28 「문제 부분만 카드형태로, 2xl 라운드」 — 그 요구가 이 모드와 정확히
 * 같은 것이라 새 모드를 만들지 않고 이것을 쓴다. 접지 않는 것도 요구와 맞는다).
 * 카드 높이는 **축소된 높이**로 직접 준다: transform 은 자리를 줄여 주지 않아
 * 그대로 두면 카드 아래가 (1−배율)만큼 텅 빈다.
 */
function SearchCardBody({ html, fallback, hasHtml, maxScale = 1 }) {
  const { hostRef, innerRef, scale, scaledH } = usePaperFit(maxScale);
  return (
    <div className={SEARCH_CARD}>
      {/* 재는 자리 = 여백 **안쪽**. 카드 자체를 재면 p-2.5 만큼 넓게 나와 본문이 오른쪽으로 넘친다.
          높이는 축소된 높이로 직접 준다(transform 은 자리를 줄여 주지 않는다).
          아직 못 쟀으면(첫 페인트) 지정하지 않는다 — 0 을 주면 한 프레임 동안 사라진다. */}
      {/* relative — 안쪽 지면이 absolute 로 뜬다. 기준점이 없으면 카드 밖으로 새어 나간다.
          overflow-hidden 은 축소 뒤에도 남는 오른쪽 잔여를 자른다(배율이 1 일 때는 자를 것이 없다). */}
      <div ref={hostRef} className="relative overflow-hidden" style={scaledH > 0 ? { height: scaledH } : undefined}>
        <PaperWidthBody innerRef={innerRef} scale={scale} html={html} fallback={fallback} hasHtml={hasHtml} hasFallback />
      </div>
    </div>
  );
}

/**
 * 좁은 목록용 본문 칸 + [본문 모두 보기] (오너 지시 2026-08-28 「내용이 생략됐으면 하단에
 * 버튼 만들어서 누르면 모두 표시되도록」).
 *
 * 버튼은 **실제로 잘렸을 때만** 낸다. 늘 띄우면 다 보이는 본문에도 「더 있다」고 말하는 셈이라
 * 화면이 거짓말을 한다. 판정은 **축소된 높이와 칸 높이를 숫자로** 견준다 —
 * scrollHeight 로 재던 종전 방식은 안쪽이 transform 으로 축소된 뒤로는 못 쓴다
 * (변형된 자식이 스크롤 영역에 어떻게 잡히는지가 브라우저마다 다르다).
 */
/** className 이 있을 때만 div 한 겹을 두른다 — 없으면 자식을 그대로 흘려보낸다 */
function Wrap({ className, children }) {
  if (!className) return children;
  return <div className={className}>{children}</div>;
}

function CompactBody({ html, fallback, hasHtml, hasFallback, showToggle = true, maxScale = 1, fill = false, clamp = true, expanded, onExpandedChange }) {
  const { hostRef, innerRef, scale, scaledH } = usePaperFit(maxScale);
  const [localOpen, setLocalOpen] = useState(false);
  const controlled = typeof expanded === 'boolean';
  const open = controlled ? expanded : localOpen;
  /**
   * clamp=false — **높이의 못을 뺀다**(오너 지시 2026-08-30 「작아졌을 때는 잘리지 않고 다
   * 보이도록」). 펴 놓은 것과 같은 상태이되 **접을 수 없다.** 접을 것이 없으니 흐림막도
   * 토글도 내지 않는다.
   *
   * 무엇을 내주는지 적어 둔다 — h-16 이 상한이자 하한이던 이유가 그대로 사라진다(위 ①).
   * 행 높이가 본문 길이를 따라가므로 목록이 계단처럼 서고, 본문이 뒤늦게 도착하는 행은
   * 그때 한 번 뛴다. 좁은 카드 목록은 한 화면에 몇 줄만 서는 자리라 그 값을 치르고
   * **본문을 다 보는 쪽**을 골랐다. 표(fill)는 행 높이를 옆 칸이 정하므로 해당 없다.
   */
  const shown = open || !clamp;

  // 행의 내용이 바뀌면(다른 문제로 갈아끼움·순서 이동) 펼친 상태를 접는다 —
  // 안 접으면 목록이 통째로 길어진 채 남아, 무엇을 폈는지 알 수 없게 된다.
  // 외부에서 관리하는 펼침 상태는 본문 응답이 늦게 도착해도 유지한다.
  useEffect(() => { setLocalOpen(false); }, [html, fallback]);

  // 칸의 실제 높이 — rem 이 걸린 값이라 px 상수로 셈하면 어긋난다(COMPACT_FALLBACK_H 주석).
  // 펼친 동안의 높이는 **칸의 크기가 아니라 내용의 크기**라 재지 않는다. 그래서 접었을 때의
  // 값이 그대로 남고, 접는 순간 다시 그 값으로 판정한다 — 버튼이 한 프레임 깜빡이지 않는다.
  const [boxH, setBoxH] = useState(0);
  useLayoutEffect(() => {
    if (shown) return;
    const h = hostRef.current == null ? 0 : hostRef.current.clientHeight;
    if (h) setBoxH((prev) => (prev === h ? prev : h));
    // 칸의 높이가 바뀌는 계기는 펴고 접는 것뿐이다(h-16 은 고정값이다) — 매 렌더 잴 필요가 없다
  }, [shown, hostRef]);

  const clipped = scaledH > (boxH || COMPACT_FALLBACK_H) + 2; // scaledH 에 이미 +1 이 얹혀 있다(usePaperFit)
  const toggle = useCallback((e) => {
    // 이 칸은 교체 모드에서 button(Slot) 안에 들어간다. 그대로 두면 버튼 안의 버튼이 되어
    // 누를 때 바깥의 '자리 맞바꾸기'까지 함께 돈다. 그래서 전파를 끊는다.
    e.preventDefault();
    e.stopPropagation();
    if (controlled) onExpandedChange?.(!open);
    else setLocalOpen((v) => !v);
  }, [controlled, open, onExpandedChange]);

  return (
    <>
      {/* ⚠ 흐림막은 **이 칸(host)에 직접** 건다. 종전에는 안쪽에 마스크 전용 div 를 한 겹 두었는데,
          지면을 absolute 로 흐름에서 빼면서(2026-08-29) 그 div 의 높이가 **0** 이 됐다 —
          마스크 영역이 0 이면 자식이 통째로 투명해져 **본문이 아예 안 보인다**(개발서버 실사용
          보고로 잡았다: 표와 좁은 카드 양쪽). host 는 h-16·h-full 로 높이가 있으니 안전하다.
          배경이 없는 상자라 마스크가 다른 것을 지울 일도 없다. */}
      {/* fill(표)은 상자를 두르지 않는다 — 칸이 이미 격자로 갈려 있고, 래퍼를 한 겹 넣으면
          위에서 내려오는 h-full 체인이 거기서 끊긴다. */}
      <Wrap className={fill ? undefined : COMPACT_CARD}>
        <div
          ref={hostRef}
          className={`${fill ? COMPACT_BOX_FILL : (shown ? COMPACT_BOX_OPEN : COMPACT_BOX)}${
            fill || !shown ? ` ${COMPACT_FADE_MASK}` : ''
          }`}
          // 펴면 축소된 높이 그대로, 접으면 h-16 이 이긴다(클래스가 style 보다 뒤에 오지 않도록 style 로만 준다).
          // fill 은 높이를 위에서 받으므로 여기서 아무것도 주지 않는다 — 주면 그 값이 h-full 을 이긴다.
          style={!fill && shown && scaledH > 0 ? { height: scaledH } : undefined}
        >
          <PaperWidthBody
            innerRef={innerRef}
            scale={scale}
            html={html}
            fallback={fallback}
            hasHtml={hasHtml}
            hasFallback={hasFallback}
          />
        </div>
      </Wrap>
      {/* showToggle=false 는 이 칸이 **버튼 안에** 놓일 때다(교체 모드의 행 전체가 button 이다).
          거기에 버튼을 또 두면 React 가 `<button> cannot appear as a descendant of <button>` 을
          내고 보조기기에서 두 조작이 한 덩어리로 읽힌다. 그 모드는 자리를 맞바꾸는 잠깐이라
          본문을 펴는 일이 없으므로 버튼을 내지 않는다. */}
      {/* fill 은 높이를 행에서 받으므로 펴고 접을 것이 없다 — 버튼을 내지 않는다.
          잘린 것은 아래 흐림막이 말하고, 다 보려면 행을 열어 상세로 간다. */}
      {!fill && clamp && showToggle && (clipped || open) && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="mt-0.5 flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          {open ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          {open ? '본문 접기' : '본문 모두 보기'}
        </button>
      )}
    </>
  );
}

export default ProblemBodyPreview;
